/**
 * Shared reminder UX for overdue invoices.
 *
 * Extracted from `CustomerInvoicesPage.jsx` so the same modal, the
 * same POST call, the same field validation and the same optimistic
 * refresh callback can be reused wherever an overdue-invoice list
 * needs a "Send reminder" action (customer-side Invoices page +
 * admin-side `Companies → [Company] → Invoices & Credit` tab).
 *
 * Usage:
 *
 *   const { openReminderModal, ReminderModal } = useInvoiceReminder({
 *     onSent: () => refetchInvoices(),   // fires only after a successful send
 *     defaultRecipient: '',              // optional fallback when the invoice
 *                                        // row has no customer_email
 *   });
 *
 *   <Button onClick={() => openReminderModal(inv)}>Send reminder</Button>
 *   {ReminderModal}
 *
 * The hook keeps its own open-state internally so callers don't have
 * to plumb `open` / `onOpenChange` themselves.
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Bell } from 'lucide-react';
import { useTranslation } from '../contexts/TranslationContext';
import { API_BASE } from '../lib/apiBase';


function formatCurrency(amount, currency = 'DKK') {
    if (amount == null) return '-';
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency }).format(amount);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' });
}


export function useInvoiceReminder({ onSent, defaultRecipient = '' } = {}) {
    const { t } = useTranslation();
    // Target row (null = modal closed).
    const [reminderTarget, setReminderTarget] = useState(null);
    // Prefilled from customer_email on the invoice row when available; the
    // admin can override the value before confirming the send.
    const [reminderRecipient, setReminderRecipient] = useState('');
    const [sendingReminder, setSendingReminder] = useState(false);

    const openReminderModal = useCallback((inv) => {
        setReminderTarget(inv);
        setReminderRecipient(inv.customer_email || defaultRecipient || '');
    }, [defaultRecipient]);

    const closeModal = useCallback(() => {
        if (!sendingReminder) setReminderTarget(null);
    }, [sendingReminder]);

    const sendReminder = useCallback(async () => {
        if (!reminderTarget) return;
        setSendingReminder(true);
        try {
            const { data } = await axios.post(
                `${API_BASE}/api/invoice-sync/invoices/${reminderTarget.economic_invoice_number}/send-payment-reminder`,
                { recipient_email: reminderRecipient || null },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
            );
            toast.success(t('invoices.reminder_sent', 'Payment reminder sent to {email}', { email: data.recipient }));
            setReminderTarget(null);
            if (typeof onSent === 'function') onSent();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('invoices.reminder_failed', 'Failed to send reminder'));
        } finally {
            setSendingReminder(false);
        }
    }, [reminderTarget, reminderRecipient, onSent, t]);

    const ReminderModal = (
        <Dialog open={!!reminderTarget} onOpenChange={(open) => !open && closeModal()}>
            <DialogContent className="max-w-md" data-testid="reminder-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5 text-red-600" />
                        {t('invoices.reminder_title', 'Send payment reminder')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('invoices.reminder_desc', 'The customer will receive an email through the standard Owntune email system. This action is logged in Email Logs.')}
                    </DialogDescription>
                </DialogHeader>

                {reminderTarget && (
                    <div className="space-y-3">
                        <div className="border rounded-md divide-y text-sm">
                            <div className="flex justify-between px-3 py-2">
                                <span className="text-slate-500">{t('invoices.col_number', 'Invoice #')}</span>
                                <span className="font-medium" data-testid="reminder-invoice-number">{reminderTarget.economic_invoice_number}</span>
                            </div>
                            <div className="flex justify-between px-3 py-2">
                                <span className="text-slate-500">{t('invoices.col_due_date', 'Due Date')}</span>
                                <span className="font-medium">{formatDate(reminderTarget.due_date)}</span>
                            </div>
                            <div className="flex justify-between px-3 py-2">
                                <span className="text-slate-500">{t('invoices.days_overdue_label', 'Days overdue')}</span>
                                <span className="font-medium text-red-600" data-testid="reminder-days-overdue">
                                    {reminderTarget.days_overdue ?? 0}
                                </span>
                            </div>
                            <div className="flex justify-between px-3 py-2">
                                <span className="text-slate-500">{t('invoices.col_outstanding', 'Outstanding')}</span>
                                <span className="font-semibold" data-testid="reminder-outstanding">
                                    {formatCurrency(reminderTarget.remainder ?? reminderTarget.gross_amount, reminderTarget.currency)}
                                </span>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                                {t('invoices.reminder_recipient', 'Recipient')}
                            </label>
                            <input
                                type="email"
                                value={reminderRecipient}
                                onChange={(e) => setReminderRecipient(e.target.value)}
                                className="w-full border rounded-md px-3 py-1.5 text-sm"
                                placeholder={t('invoices.reminder_recipient_placeholder', 'customer@example.com')}
                                data-testid="reminder-recipient"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">
                                {t('invoices.reminder_recipient_hint', 'Prefilled from the customer contact on file. Edit if needed.')}
                            </p>
                        </div>
                        {reminderTarget.last_reminder_sent_at && (
                            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
                                {t('invoices.reminder_previously_sent', 'A reminder was already sent {date} by {name}.', {
                                    date: formatDate(reminderTarget.last_reminder_sent_at),
                                    name: reminderTarget.last_reminder_sent_by_name || 'admin',
                                })}
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={closeModal} disabled={sendingReminder} data-testid="reminder-cancel">
                        {t('invoices.reminder_cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={sendReminder}
                        disabled={sendingReminder || !reminderRecipient}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="reminder-confirm"
                    >
                        {sendingReminder
                            ? t('invoices.reminder_sending', 'Sending…')
                            : t('invoices.reminder_send', 'Send reminder')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    return { openReminderModal, ReminderModal, sendingReminder };
}
